// F148: `stripe_import_events.detail` carries a SET of codes, not one.
//
// Until F148 the column held a single internal code and the two consoles read
// it with `===` and `startsWith`. One purchase can now carry several facts at
// once - the configuration came from the product's metadata AND the default
// partner setting is stale, or two of the buyer's products declared themselves
// to Launchpad and one of them is unreadable - and a column that holds one
// string would silently drop whichever fact lost the precedence chain. A fact
// that disappears under another fact is the same lie a count that disagrees
// with its own list tells (S2-05/S3-02), one column over.
//
// The encoding is deliberately the smallest thing that cannot break the rows
// already written: codes are `[a-z0-9_]+` joined by a single space, so every
// pre-F148 value (`missing_email`, `stripe_api_404`, `session_replay`) parses
// as a one-element set and every existing reader keeps its meaning. No
// migration: the column is TEXT with no CHECK.

/** A code is a machine name, never a sentence and never operator input. */
const CODE_RE = /^[a-z0-9_]+$/;

/**
 * Join the codes into the stored string. Order is preserved (the first code is
 * the most important one), duplicates and anything that is not a machine code
 * are dropped, and an empty set is `null` - the column's own "nothing to say".
 */
export function encodeImportDetail(codes: readonly (string | null | undefined)[]): string | null {
  const seen: string[] = [];
  for (const code of codes) {
    if (typeof code !== "string") continue;
    const trimmed = code.trim();
    if (!CODE_RE.test(trimmed) || seen.includes(trimmed)) continue;
    seen.push(trimmed);
  }
  return seen.length ? seen.join(" ") : null;
}

/** Read the stored string back as the set of codes it stands for. */
export function importDetailCodes(detail: string | null | undefined): string[] {
  if (typeof detail !== "string") return [];
  return detail.split(/\s+/).map((code) => code.trim()).filter((code) => CODE_RE.test(code));
}

export function hasImportDetail(detail: string | null | undefined, code: string): boolean {
  return importDetailCodes(detail).includes(code);
}

/**
 * The first code starting with `prefix`, for the families whose tail is not a
 * fixed list (`stripe_api_402`, `create_state_conflict`).
 */
export function importDetailWithPrefix(detail: string | null | undefined, prefix: string): string | undefined {
  return importDetailCodes(detail).find((code) => code.startsWith(prefix));
}

/**
 * The configuration channel a purchase was read through, recorded on the event
 * so Servis can say it. Absent = the legacy `stripe_product_mappings` row,
 * which is what every event written before F148 was configured by.
 */
export const DETAIL_CONFIG_METADATA = "config_metadata";
